import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Input, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError, showToast } from '../../constants/toasts';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useAddVehicleMutation,
  useLazyGetVehicleTypeByIdQuery,
  useUpdateVehicleTypeMutation,
} from '../../service/vehicleType';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { UploadMedia } from '../../utils/mediaUpload';

const AddVehicleTypes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile_picture, setProfilePicture] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [addVehicle] = useAddVehicleMutation();
  const [updateVehicle] = useUpdateVehicleTypeMutation();
  const [getDetail] = useLazyGetVehicleTypeByIdQuery();

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfilePicture('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setProfilePicture(imageUrl?.data?.image || '');
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        image: profile_picture,
        name: values?.name || '',
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateVehicle({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Vehicle type updated successfully');
            navigate('/manage-vehicle-types');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addVehicle({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Vehicle type added successfully');
            navigate('/manage-vehicle-types');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getVehicleType = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('name', response?.data?.name);
        setProfilePicture(response?.data?.image);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getVehicleType(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Vehicle Type</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/manage-vehicle-types')}>Vehicle Types</span> -{' '}
            {id ? 'Edit' : 'Add'} Vehicle Type
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Image</label>
              <div className="upload_box">
                <label>
                  {profile_picture && (
                    <IconButton className="crossIcon" onClick={() => setProfilePicture('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={profile_picture ? profile_picture : '/static/images/picture.png'}
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file-profile">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file-profile"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleImageUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
                {error && !profile_picture ? <h6 className="err_msg">Image is required</h6> : null}
              </div>
            </div>

            <div className="control_group w_50">
              <label>Vehicle Type</label>
              <TextField
                variant="outlined"
                placeholder="Vehicle Type"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                className="text_field"
                inputProps={{ maxLength: 20 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit" onClick={() => setError(true)}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddVehicleTypes;
